import {
  BarChart3,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  Contact,
  CalendarRange,
  CheckSquare,
  ClipboardList,
  FileText,
  FolderKanban,
  Layers,
  LayoutGrid,
  Library,
  Lock,
  Mail,
  Package,
  Radio,
  Receipt,
  Settings,
  SlidersHorizontal,
  Truck,
  UserCog,
  Users,
  Wrench,
} from "lucide-react";
import type { ComponentType } from "react";
import type { LucideIcon } from "lucide-react";
import { isPathWithin } from "./paths";
import { tools, TOOLS_OVERVIEW_HREF, TOOLS_SECTION_TITLE } from "./tools";

export interface MainNavSubItem {
  title: string;
  href: string;
  icon: LucideIcon;
  employeeOnly?: boolean;
  adminOnly?: boolean;
  /** Only shown where dev tools are enabled (dev-env, previews) */
  devToolsOnly?: boolean;
  /** Absolute URL to another app, opened in a new tab */
  external?: boolean;
  /** When it returns a URL, the entry opens it in a new tab instead of `href` */
  externalHref?: () => string | undefined;
}

export interface MainNavItem {
  title: string;
  icon: LucideIcon;
  href?: string;
  items?: MainNavSubItem[];
  /** Rendered after `items` inside the expanded group */
  extraSubItems?: ComponentType<{ onNavigate: () => void }>;
  employeeOnly?: boolean;
  adminOnly?: boolean;
  /** Only shown where dev tools are enabled (dev-env, previews) */
  devToolsOnly?: boolean;
}

/** A labelled group of the main navigation; hidden when no item is visible. */
export interface MainNavSection {
  title: string;
  items: MainNavItem[];
}

// The dashboard has no item: the logo and app name in the sidebar header
// link to it.
export const mainNavigation: MainNavSection[] = [
  {
    title: "Projects & People",
    items: [
      {
        title: "Projects",
        icon: FolderKanban,
        items: [
          { title: "List", href: "/projects", icon: FolderKanban },
          {
            title: "Project Groups",
            href: "/projects/groups",
            icon: Layers,
            employeeOnly: true,
          },
          {
            title: "Companies",
            href: "/companies",
            icon: Building2,
            employeeOnly: true,
          },
        ],
      },
      { title: "Calendar", icon: Calendar, href: "/calendar" },
      {
        title: "People",
        icon: Users,
        employeeOnly: true,
        items: [
          { title: "All", href: "/people", icon: Users },
          { title: "Employees", href: "/people/employees", icon: UserCog },
          {
            title: "Freelancers",
            href: "/people/freelancers",
            icon: Briefcase,
          },
          {
            title: "Crew Planning",
            href: "/crew-planning",
            icon: ClipboardList,
          },
        ],
      },
    ],
  },
  {
    title: "Systems",
    items: [
      {
        title: "Equipment",
        icon: Package,
        employeeOnly: true,
        items: [{ title: "Overview", href: "/equipment", icon: LayoutGrid }],
      },
      {
        title: "Vehicles",
        icon: Truck,
        employeeOnly: true,
        items: [
          { title: "Overview", href: "/vehicles", icon: LayoutGrid },
          {
            title: "Planning",
            href: "/vehicles/planning",
            icon: CalendarRange,
          },
        ],
      },
      { title: "Studios", icon: Radio, href: "/studios", employeeOnly: true },
    ],
  },
  {
    title: "Operations",
    items: [
      { title: "Docs", icon: BookOpen, href: "/docs", employeeOnly: true },
      {
        title: "Meeting Notes",
        icon: FileText,
        href: "/meeting-notes",
        employeeOnly: true,
      },
      { title: "Tasks", icon: CheckSquare, href: "/tasks", employeeOnly: true },
      {
        title: "Worktime & Vacation",
        href: "/my-balance",
        icon: BarChart3,
        employeeOnly: true,
      },
    ],
  },
  {
    title: TOOLS_SECTION_TITLE,
    items: [
      {
        title: "All Tools",
        icon: LayoutGrid,
        href: TOOLS_OVERVIEW_HREF,
        employeeOnly: true,
      },
    ],
  },
  {
    title: "Management",
    items: [
      {
        title: "HR",
        icon: UserCog,
        adminOnly: true,
        items: [
          { title: "Overtime View", href: "/hr", icon: ClipboardList },
          { title: "Settings", href: "/settings", icon: SlidersHorizontal },
        ],
      },
      { title: "Finance", icon: Receipt, href: "/finance", employeeOnly: true },
      {
        title: "Management Meeting Notes",
        icon: Lock,
        href: "/meeting-notes/management",
        adminOnly: true,
      },
    ],
  },
  {
    title: "Settings",
    items: [
      {
        title: "App Settings",
        href: "/admin/settings",
        icon: Settings,
        adminOnly: true,
      },
      {
        title: "Default Functions",
        href: "/admin/default-functions",
        icon: Contact,
        adminOnly: true,
      },
      {
        title: "Quote Templates",
        href: "/admin/quote-templates",
        icon: Library,
        adminOnly: true,
      },
      {
        title: "Maintenance",
        href: "/admin/maintenance",
        icon: Wrench,
        adminOnly: true,
      },
      {
        title: "Mail Catcher",
        href: "/admin/mail-catcher",
        icon: Mail,
        adminOnly: true,
        devToolsOnly: true,
      },
    ],
  },
];

/** All items of the main navigation, across sections. */
export const mainNavItems: MainNavItem[] = mainNavigation.flatMap(
  (section) => section.items,
);

/** Planning pages open people, vehicles and projects in the panel (K3). */
export const PLANNING_PATHS = [
  "/crew-planning",
  "/vehicles/planning",
  "/calendar",
];

export function isPlanningPath(pathname: string) {
  return PLANNING_PATHS.some((path) => isPathWithin(pathname, path));
}

/** Best label for a main-navigation path, used when a page sets no title. */
export function mainNavLabel(pathname: string): string | null {
  let best: { title: string; length: number } | null = null;
  const consider = (title: string, href: string) => {
    if (href.startsWith("http")) return;
    const matches =
      href === "/" ? pathname === "/" : isPathWithin(pathname, href);
    if (matches && (!best || href.length > best.length)) {
      best = { title, length: href.length };
    }
  };
  consider("Dashboard", "/");
  for (const item of mainNavItems) {
    if (item.href) consider(item.title, item.href);
    for (const sub of item.items ?? []) {
      // "List" / "All" / "Overview" read better as the group name.
      const generic = ["List", "All", "Overview"].includes(sub.title);
      consider(generic ? item.title : sub.title, sub.href);
    }
  }
  for (const tool of tools) consider(tool.title, tool.href);
  return (best as { title: string } | null)?.title ?? null;
}
